import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  Skeleton,
  Tooltip,
  Divider,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import {
  DotsThreeVerticalIcon,
  RobotIcon,
  NotebookIcon,
  TrashSimpleIcon,
  CopyIcon,
  CheckIcon,
  ForkKnifeIcon,
  SealQuestionIcon,
  ChairIcon,
  PencilSimpleIcon,
  PhoneIcon,
} from '@phosphor-icons/react';
import { useAgents } from '../../hooks/useAgents';
import { format } from 'date-fns';
import LayoutWithSidebar from '../../components/common/layoutWithSidebar';
import { useNavigate } from 'react-router-dom';
import { useDashboard } from '../../hooks/useDashboard';
import { Agent } from '../../Providers/DashboardProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteAgentApi } from '../../utils/apis';
import { useAlert } from '../../hooks/useAlert';
import MessagePopup from '../../components/common/MessagePopup';
import DeleteModal from '../../components/modals/DeleteModal';
import { routes } from '../../constants/routes';
import { useTemplates } from '../../hooks/useTemplates';
import { HeaderContainer } from '../../components/dashboard/AgentInfoHeader';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import NoDataFallback from '../../components/common/NoDataFallback';
import AddNewAssistant from '../AddNewAssistant';
import EditAgentTypeModal from '../../components/modals/EditAgentTypeModal';
import UpdatePhoneAccountTypeModal from '../../components/modals/UpdatePhoneAccountTypeModal';
import useHasAccess from '../../hooks/useHasAccess';
import { normalizePhoneAccountType } from '../../utils/phoneAccountType';

const AgentsScreen = () => {
  const { agents, isLoading, isError, error } = useAgents();
  const { data: templatesData } = useTemplates();
  const navigate = useNavigate();
  const { setSelectedAssistant, setSelectedAssistantId } = useDashboard();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedAgentId, setSelectedAgentId] = React.useState<string | null>(
    null
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = React.useState(false);
  const { alert, setAlert, showAlert } = useAlert();
  const queryClient = useQueryClient();
  const { copy } = useCopyToClipboard();
  const [copiedId, setCopiedId] = React.useState<string | null>(null);
  const [isEditAgentTypeModalOpen, setIsEditAgentTypeModalOpen] =
    React.useState(false);
  const [editAgentData, setEditAgentData] = React.useState<{
    agentId: string;
    templateId: string;
  } | null>(null);
  const [isPhoneAccountTypeModalOpen, setIsPhoneAccountTypeModalOpen] =
    React.useState(false);
  const [phoneAccountEdit, setPhoneAccountEdit] = React.useState<{
    agentId: string;
    phoneAccountType?: string | null;
  } | null>(null);
  const hasCallLogsAccess = useHasAccess('call_logs');

  const { mutateAsync: deleteAgent, isPending: isDeletePending } = useMutation({
    mutationFn: deleteAgentApi,
    onSuccess: () => {
      showAlert('Agent deleted successfully', 'success');
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      handleMenuClose();
    },
    onError: (error: any) => {
      showAlert(
        error?.response?.data?.message || 'Failed to delete agent',
        'error'
      );
    },
  });

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    agentId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedAgentId(agentId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedAgentId(null);
  };

  const handleViewCallLogs = () => {
    const selectedAgent = agents?.agents?.find(
      (agent: Agent) => agent._id === selectedAgentId
    );
    if (selectedAgent) {
      localStorage.setItem('selectedAgent', JSON.stringify(selectedAgent));
      navigate(routes.calls);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    if (selectedAgentId) {
      await deleteAgent(selectedAgentId);
      setIsDeleteModalOpen(false);
    }
  };

  const handleAgentClick = (agent: Agent) => {
    setSelectedAssistant(agent);
    setSelectedAssistantId(agent._id);
    const formattedName = agent.agentName?.toLowerCase().replace(/\s+/g, '-');
    navigate(`/agent/${encodeURIComponent(formattedName)}`);
  };

  const getTemplateIcon = (templateId: string) => {
    const template = templatesData?.data?.templates?.find(
      (t: any) => t._id === templateId
    );

    switch (template?.name) {
      case 'Food Ordering':
        return <ForkKnifeIcon size={12} />;
      case 'General Queries':
        return <SealQuestionIcon size={12} />;
      case 'Automate Reservation':
        return <ChairIcon size={12} />;
      default:
        return null;
    }
  };

  const getTemplateName = (templateId: string) => {
    const template = templatesData?.data?.templates?.find(
      (t: any) => t._id === templateId
    );
    switch (template?.name) {
      case 'Food Ordering':
        return 'Food Ordering';
      case 'General Queries':
        return 'General FAQ';
      case 'Automate Reservation':
        return 'Reservation';
      default:
        return null;
    }
  };

  const filteredAgents =
    agents?.agents?.filter((agent: Agent) => agent.agentName?.trim()) || [];

  const handleCopyClick = (id: string, event: React.MouseEvent) => {
    event.stopPropagation();
    copy(id);
    setCopiedId(id);
    setTimeout(() => {
      setCopiedId(null);
    }, 2000);
  };

  const handleEditAgentType = () => {
    if (selectedAgentId) {
      const selectedAgent = agents?.agents?.find(
        (agent: Agent) => agent._id === selectedAgentId
      );
      if (selectedAgent) {
        setEditAgentData({
          agentId: selectedAgent._id,
          templateId: selectedAgent.templateId || '',
        });
        setIsEditAgentTypeModalOpen(true);
      }
    }
    handleMenuClose();
  };

  const handleUpdatePhoneAccountType = () => {
    if (selectedAgentId) {
      const selectedAgent = agents?.agents?.find(
        (agent: Agent) => agent._id === selectedAgentId
      );
      if (selectedAgent) {
        setPhoneAccountEdit({
          agentId: selectedAgent._id,
          phoneAccountType: selectedAgent.phoneAccountType ?? null,
        });
        setIsPhoneAccountTypeModalOpen(true);
      }
    }
    handleMenuClose();
  };

  const handleCopyAgentKey = () => {
    const selectedAgent = agents?.agents?.find(
      (agent: Agent) => agent._id === selectedAgentId
    );
    if (selectedAgent && selectedAgent.agentKey) {
      copy(selectedAgent.agentKey);
      showAlert('Agent key copied to clipboard', 'success');
    } else {
      showAlert('Agent key not found', 'error');
    }
    handleMenuClose();
  };

  const selectedAgent = agents?.agents?.find(
    (agent: Agent) => agent._id === selectedAgentId
  );
  const selectedAgentPhoneType = normalizePhoneAccountType(
    selectedAgent?.phoneAccountType
  );
  const phoneAccountActionLabel =
    selectedAgentPhoneType === 'prod'
      ? 'Update to Trial'
      : selectedAgentPhoneType === 'trial'
        ? 'Upgrade to Production'
        : 'Update Phone Account Type';

  const columns: GridColDef[] = [
    {
      field: 'icon',
      headerName: '',
      width: 50,
      flex: 0.1,
      sortable: false,
      filterable: false,
      align: 'center',
      headerClassName: 'custom-header',
      renderCell: () => (
        <Box
          sx={{
            width: 24,
            height: 24,
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '4px',
            borderRadius: '6px',
            border: '1px solid #DCE0E7',
            bgcolor: '#F8F9FC',
          }}
        >
          <RobotIcon size={16} />
        </Box>
      ),
    },
    {
      field: 'agentName',
      headerName: 'Agent Name',
      minWidth: 200,
      flex: 1,
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => (
        <Box
          component="button"
          onClick={() => handleAgentClick(params.row)}
          sx={{
            color: 'primary.main',
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            cursor: 'pointer',
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline',
            },
          }}
        >
          <Tooltip title="View Details">
            <Typography
              sx={{
                overflow: 'hidden',
                color: '#6E61F0',
                textAlign: 'center',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontFamily: '"DM Sans"',
                fontSize: '14px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                letterSpacing: '-0.14px',
                width: '100%',
                maxWidth: '170px',
              }}
            >
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: '_id',
      headerName: 'Agent ID',
      minWidth: 250,
      flex: 1.5,
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            width: '100%',
          }}
        >
          <Typography
            sx={{
              overflow: 'hidden',
              color: '#6B6F80',
              textOverflow: 'ellipsis',
              fontFamily: '"DM Sans"',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
              letterSpacing: '-0.14px',
            }}
          >
            {params.value}
          </Typography>
          <IconButton
            size="small"
            onClick={e => handleCopyClick(params.value, e)}
            sx={{
              p: 0.5,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            {copiedId === params.value ? (
              <CheckIcon size={14} color="#37B77D" />
            ) : (
              <CopyIcon size={14} color="#6B6F80" />
            )}
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'templateId',
      headerName: 'Type',
      minWidth: 180,
      flex: 0.8,
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '115px',
              gap: 1,
              color: '#6B6F80',
              border: '1px solid #DCE0E7',
              padding: '8px',
              borderRadius: '6px',
              bgcolor: '#FFF',
            }}
          >
            {getTemplateIcon(params.value)}
            <Typography
              sx={{
                fontFamily: '"DM Sans"',
                fontSize: '12px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: 'normal',
                letterSpacing: '-0.14px',
              }}
            >
              {getTemplateName(params.value)}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'agentChannel',
      headerName: 'Channel',
      minWidth: 120,
      flex: 0.8,
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => {
        const channel =
          params.row.agentChannel === 'whatsapp' ? 'Whatsapp' : 'Voice';
        return (
          <Typography
            sx={{
              color: '#6B6F80',
              fontFamily: '"DM Sans"',
              fontSize: '14px',
              fontStyle: 'normal',
              fontWeight: 400,
              lineHeight: 'normal',
              letterSpacing: '-0.14px',
            }}
          >
            {channel}
          </Typography>
        );
      },
    },
    {
      field: 'phoneAccountType',
      headerName: 'Status',
      minWidth: 120,
      flex: 0.8,
      headerAlign: 'center',
      align: 'center',
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => {
        const normalized = normalizePhoneAccountType(
          params.row.phoneAccountType
        );
        const label =
          normalized === 'trial'
            ? 'Trial'
            : normalized === 'prod'
              ? 'Prod'
              : '-';
        const chipStyles =
          normalized === 'trial'
            ? {
                color: '#9A3412',
                backgroundColor: '#FFF7ED',
                borderColor: '#FDBA74',
              }
            : normalized === 'prod'
              ? {
                  color: '#166534',
                  backgroundColor: '#F0FDF4',
                  borderColor: '#86EFAC',
                }
              : {
                  color: '#6B7280',
                  backgroundColor: '#F9FAFB',
                  borderColor: '#D1D5DB',
                };

        return (
          <Chip
            label={label}
            size="small"
            sx={{
              fontFamily: '"DM Sans"',
              fontSize: '12px',
              fontWeight: 600,
              letterSpacing: '-0.14px',
              height: '24px',
              borderRadius: '999px',
              border: '1px solid',
              ...chipStyles,
              '& .MuiChip-label': {
                px: 1.2,
                fontStyle: 'italic',
              },
            }}
          />
        );
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created On',
      minWidth: 150,
      flex: 0.8,
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => (
        <Typography
          sx={{
            overflow: 'hidden',
            color: '#6B6F80',
            textAlign: 'center',
            textOverflow: 'ellipsis',
            fontFamily: '"DM Sans"',
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: 400,
            lineHeight: 'normal',
            letterSpacing: '-0.14px',
          }}
        >
          {params.value ? format(new Date(params.value), 'MMM dd, yyyy') : '-'}
        </Typography>
      ),
    },

    {
      field: 'actions',
      headerName: 'Action',
      minWidth: 80,
      flex: 0.8,
      sortable: false,
      filterable: false,
      headerAlign: 'center',
      align: 'center',
      headerClassName: 'custom-header',
      renderCell: (params: GridRenderCellParams) => (
        <IconButton
          onClick={e => handleMenuClick(e, params.row._id)}
          size="small"
        >
          <DotsThreeVerticalIcon />
        </IconButton>
      ),
    },
  ];

  if (
    (!agents ||
      !agents?.agents?.some((agent: any) => agent.agentName !== '') ||
      agents?.agents?.length === 0) &&
    !isLoading
  ) {
    return <AddNewAssistant />;
  }

  return (
    <LayoutWithSidebar margin={2}>
      {isLoading ? (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            border: '1px solid #DCE0E7',
            borderRadius: '8px',
            overflow: 'hidden',
            bgcolor: 'background.paper',
          }}
        >
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: '1px solid #DCE0E7' }}>
            <Skeleton variant="text" width={150} height={32} />
          </Box>

          {/* Table Content */}
          <Box sx={{ p: 2 }}>
            {/* Column Headers */}
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                mb: 2,
                bgcolor: '#F8F9FC',
                p: 2,
                borderRadius: '8px',
              }}
            >
              <Skeleton variant="rectangular" width={50} height={24} />
              <Skeleton variant="rectangular" width={200} height={24} />
              <Skeleton variant="rectangular" width={250} height={24} />
              <Skeleton variant="rectangular" width={150} height={24} />
              <Skeleton variant="rectangular" width={120} height={24} />
              <Skeleton variant="rectangular" width={150} height={24} />
              <Skeleton variant="rectangular" width={80} height={24} />
            </Box>

            {/* Table Rows */}
            {[...Array(5)].map((_, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  gap: 2,
                  p: 2,
                  borderBottom: '1px solid #DCE0E7',
                  alignItems: 'center',
                }}
              >
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '4px',
                    borderRadius: '6px',
                    border: '1px solid #DCE0E7',
                    bgcolor: '#F8F9FC',
                  }}
                >
                  <Skeleton variant="rectangular" width={16} height={16} />
                </Box>
                <Skeleton variant="text" width={200} />
                <Skeleton variant="text" width={250} />
                <Skeleton variant="text" width={150} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Skeleton variant="circular" width={8} height={8} />
                  <Skeleton variant="text" width={80} />
                </Box>
                <Skeleton variant="text" width={150} />
                <Skeleton variant="circular" width={24} height={24} />
              </Box>
            ))}
          </Box>

          {/* Pagination */}
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid #DCE0E7',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Skeleton variant="text" width={100} />
            <Skeleton variant="text" width={50} />
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            height: '100%',
            gap: 2,
            bgcolor: '#F8F9FC',
          }}
        >
          <HeaderContainer>
            <Typography
              sx={{
                color: '#302B3F',
                fontFamily: '"DM Sans"',
                fontSize: '24px',
                fontStyle: 'normal',
                fontWeight: 600,
                lineHeight: 'normal',
                letterSpacing: '-0.24px',
              }}
            >
              Agents
            </Typography>
          </HeaderContainer>

          {isError && error && isLoading && (
            <NoDataFallback
              httpStatusCode={(error as any)?.status}
              message="No agents found"
              description="No agents are available right now Create a new agent to get started"
            />
          )}

          <Box
            sx={{
              width: '100%',
              height: '100%',
              border: '1px solid #DCE0E7',
              borderRadius: '8px',
              overflow: 'hidden',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#FFFFFF',
            }}
          >
            <DataGrid
              rows={filteredAgents}
              columns={columns}
              getRowId={row => row._id}
              initialState={{
                pagination: {
                  paginationModel: { page: 0, pageSize: 100 },
                },
              }}
              pageSizeOptions={[10, 20, 50, 75, 100]}
              disableRowSelectionOnClick
              loading={isLoading}
              sx={{
                '& .MuiDataGrid-row': {
                  alignItems: 'center',
                },
                '& .custom-header': {
                  bgcolor: '#F8F9FC',
                  color: 'custom.textBlue',
                  fontSize: '14px',
                  fontWeight: 600,
                },
                '& .MuiDataGrid-cell': {
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  '&:focus, &:focus-within': {
                    outline: 'none !important',
                  },
                },
                border: 'none',
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none !important',
                },
              }}
            />
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            sx={{
              '& .MuiPaper-root': {
                borderRadius: '12px',
                boxShadow: 1,
                border: '1px solid #E0E0E0',
              },
            }}
          >
            <Tooltip
              title={
                hasCallLogsAccess
                  ? ''
                  : 'You need permission to access Call Logs. Contact your organization owner.'
              }
              arrow
            >
              <span>
                <MenuItem
                  onClick={handleViewCallLogs}
                  disabled={!hasCallLogsAccess}
                  sx={{
                    fontSize: '14px',
                    color: 'custom.textBlue',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <NotebookIcon size={18} />
                  Call Logs
                </MenuItem>
              </span>
            </Tooltip>
            <Divider sx={{ mx: 2 }} />
            <MenuItem
              onClick={handleEditAgentType}
              sx={{
                color: 'custom.textBlue',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <PencilSimpleIcon size={18} />
              Edit Agent Type
            </MenuItem>
            <Divider sx={{ mx: 2 }} />
            <MenuItem
              onClick={handleUpdatePhoneAccountType}
              sx={{
                color: 'custom.textBlue',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <PhoneIcon size={18} />
              {phoneAccountActionLabel}
            </MenuItem>
            <Divider sx={{ mx: 2 }} />
            <MenuItem
              onClick={handleCopyAgentKey}
              sx={{
                color: 'custom.textBlue',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CopyIcon size={18} />
              Copy Agent Key
            </MenuItem>
            <Divider sx={{ mx: 2 }} />
            <MenuItem
              onClick={handleDeleteClick}
              sx={{
                color: 'error.main',
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <TrashSimpleIcon size={18} />
              Delete Agent
            </MenuItem>
          </Menu>

          <DeleteModal
            open={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            onDelete={handleDelete}
            isPending={isDeletePending}
            title="Delete Agent"
            description="Are you sure you want to delete this agent? This action cannot be undone."
          />

          <EditAgentTypeModal
            open={isEditAgentTypeModalOpen}
            onClose={() => {
              setIsEditAgentTypeModalOpen(false);
              setEditAgentData(null);
            }}
            agentId={editAgentData?.agentId || ''}
            currentTemplateId={editAgentData?.templateId || ''}
          />

          <UpdatePhoneAccountTypeModal
            key={
              isPhoneAccountTypeModalOpen && phoneAccountEdit?.agentId
                ? phoneAccountEdit.agentId
                : 'phone-account-type-modal-closed'
            }
            open={isPhoneAccountTypeModalOpen}
            onClose={() => {
              setIsPhoneAccountTypeModalOpen(false);
              setPhoneAccountEdit(null);
            }}
            agentId={phoneAccountEdit?.agentId || ''}
            currentPhoneAccountType={phoneAccountEdit?.phoneAccountType}
            onUpdateSuccess={message => showAlert(message, 'success')}
            onUpdateError={message => showAlert(message, 'error')}
          />

          <MessagePopup alert={alert} setAlert={setAlert} />
        </Box>
      )}
    </LayoutWithSidebar>
  );
};

export default AgentsScreen;
